import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { ChevronDown, Car, Bike } from "lucide-react";
import { useMakes } from "@/hooks/useMakes";
import { Make } from "@/types/make";
import { validateRegistrationFormatAny } from "@/utils/registrationValidation";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import {
  shouldAutoExpandOtherForUnlistedModel,
  shouldAutoExpandOtherForUnknownMake,
  isFallbackOtherChipSelected,
  normalizeOtherSubmitValue,
} from "./vehicleInfoOtherDisplay";

interface VehicleInformationSectionProps {
  make: string | undefined;
  model: string | undefined;
  variant?: string | undefined;
  year: number | string | undefined;
  onMakeSelect: (make: string) => void;
  onModelSelect: (model: string) => void;
  onVariantChange?: (variant: string) => void;
  onYearSelect: (year: number | string) => void;
  onVehicleTypeSelect?: (vehicleType: "car" | "mc") => void;
  isAutoLoaded?: boolean;
  onAutoLoadedFieldEditAttempt?: () => void;
  isManualMode?: boolean;
  regNumber?: string;
  onRegNumberChange?: (regNumber: string) => void;
}

const YEARS = Array.from({ length: 27 }, (_, i) => String(2026 - i));

type VehicleType = "car" | "mc" | null;

export const VehicleInformationSection = ({
  make,
  model,
  variant,
  year,
  onMakeSelect,
  onModelSelect,
  onVariantChange,
  onYearSelect,
  onVehicleTypeSelect,
  isAutoLoaded = false,
  onAutoLoadedFieldEditAttempt,
  isManualMode = false,
  regNumber = "",
  onRegNumberChange,
}: VehicleInformationSectionProps) => {
  const [vehicleType, setVehicleType] = useState<VehicleType>(null);
  const [selectedMakeId, setSelectedMakeId] = useState<string | null>(null);
  const [showAllMakes, setShowAllMakes] = useState(false);
  const [showAllModels, setShowAllModels] = useState(false);
  const [showAllYears, setShowAllYears] = useState(false);
  const [showMakeOtherInput, setShowMakeOtherInput] = useState(false);
  const [showModelOtherInput, setShowModelOtherInput] = useState(false);
  const [makeOtherText, setMakeOtherText] = useState("");
  const [modelOtherText, setModelOtherText] = useState("");

  const { language } = useLanguage();
  const vLabels = valuationLabels(language).vehicleInfo;

  // Always load makes data (needed for editing even in auto-loaded mode)
  const { makes, isLoadingMakes } = useMakes(true);

  // Filter makes based on vehicle type
  const filteredMakes = useMemo(() => {
    if (!vehicleType || !makes.length) return [];
    return makes.filter((m) => (vehicleType === "mc" ? m.isMC : !m.isMC));
  }, [makes, vehicleType]);

  // Get popular makes first
  const popularMakes = useMemo(() => {
    return filteredMakes.filter((m) => m.isPopular);
  }, [filteredMakes]);

  // Get non-popular makes
  const otherMakes = useMemo(() => {
    return filteredMakes.filter((m) => !m.isPopular);
  }, [filteredMakes]);

  // Display makes: show all popular makes initially, then others if showAllMakes is true
  // Always keep "Övrigt" as the last item
  // For motorcycles (mc), show all makes directly (no "Mer" button needed)
  const displayedMakes = useMemo(() => {
    // For motorcycles, always show all makes directly
    if (vehicleType === "mc") {
      return filteredMakes;
    }

    if (showAllMakes) {
      // When showing all, still put Övrigt last
      return [...popularMakes, ...otherMakes];
    }
    // Show all popular makes initially (not just 7)
    return popularMakes;
  }, [popularMakes, otherMakes, showAllMakes, vehicleType, filteredMakes]);

  // Get selected make object - try to find it even if makes are still loading
  const selectedMake = useMemo(() => {
    if (!make && !selectedMakeId) return null;
    if (!makes.length) return null;
    // Try to find by ID first (most reliable)
    if (selectedMakeId) {
      const foundById = makes.find((m) => m.id === selectedMakeId);
      if (foundById) return foundById;
    }
    // Fallback to finding by name (case-insensitive match)
    if (make) {
      const foundByName = makes.find(
        (m) => m.name.toLowerCase() === make.toLowerCase(),
      );
      if (foundByName) return foundByName;
    }
    return null;
  }, [makes, selectedMakeId, make]);

  // Get models for selected make (excluding "Övrigt")
  const availableModels = useMemo(() => {
    if (!selectedMake) return [];
    return selectedMake.models.filter((m) => m.name !== "Övrigt");
  }, [selectedMake]);

  // Display models: first 5, then all if showAllModels is true
  // Display models: show 7 models + Övrigt = 8 items (2 rows of 4 columns)
  const displayedModels = useMemo(() => {
    if (!availableModels.length) return [];
    if (showAllModels) {
      return availableModels;
    }
    // Show 7 models (to fill 2 rows of 4 columns, with Övrigt as 8th item)
    return availableModels.slice(0, 7);
  }, [availableModels, showAllModels]);

  // Reset make and model when vehicle type changes (only if user manually changes vehicleType)
  const prevVehicleTypeRef = useRef<VehicleType>(null);
  const userChangedVehicleTypeRef = useRef(false);
  const isSettingVehicleTypeFromMakeRef = useRef(false);

  useEffect(() => {
    // CRITICAL: Skip if vehicleType is being set automatically from make selection
    // This must be checked FIRST to prevent any interference
    if (isSettingVehicleTypeFromMakeRef.current) {
      return;
    }

    // Skip on initial load (when prevVehicleTypeRef is null and vehicleType is being set for first time)
    // BUT only if we don't have a make selected - if we have a make, it was set manually and vehicleType should match it
    if (!prevVehicleTypeRef.current && vehicleType) {
      // If make is already selected, this is likely from auto-setting from make selection
      // Don't update prevVehicleTypeRef yet - wait until we're sure this wasn't from make selection
      if (make || selectedMakeId) {
        // We have a make, so vehicleType was probably set from make selection
        // Keep prevVehicleTypeRef as null to allow this to be treated as initial
        return;
      }
      prevVehicleTypeRef.current = vehicleType;
      return;
    }

    // Skip if vehicleType hasn't changed
    if (prevVehicleTypeRef.current === vehicleType) {
      return;
    }

    // CRITICAL: Only reset if user manually changed vehicleType (not when it's auto-set)
    // If user didn't manually change it, just update the ref and return
    if (!userChangedVehicleTypeRef.current) {
      prevVehicleTypeRef.current = vehicleType;
      return;
    }

    // User manually changed vehicleType - check if make needs to be reset
    const prevVehicleType = prevVehicleTypeRef.current;
    prevVehicleTypeRef.current = vehicleType;
    userChangedVehicleTypeRef.current = false; // Reset flag

    // Only reset if vehicleType changed AND we have a make that doesn't match the new type
    // CRITICAL: Only check if makes are loaded AND we're sure the make exists
    if (vehicleType && (make || selectedMakeId) && makes.length > 0) {
      const currentMake = makes.find(
        (m) =>
          m.id === selectedMakeId ||
          (make && m.name.toLowerCase() === make.toLowerCase()),
      );
      // Only reset if make is found AND doesn't match vehicleType
      if (
        currentMake &&
        ((vehicleType === "mc" && !currentMake.isMC) ||
          (vehicleType === "car" && currentMake.isMC))
      ) {
        // Make doesn't match the new vehicle type - reset it
        onMakeSelect("");
        setSelectedMakeId(null);
        onModelSelect("");
      }
    }
  }, [vehicleType, make, selectedMakeId, makes, onMakeSelect, onModelSelect]);

  // When makes data loads, try to find the selected make if we have a make name but no ID
  // This is mainly for auto-loaded cases or when makes load after make is already selected
  const makeLookupKeyRef = useRef<string>("");

  useEffect(() => {
    // CRITICAL: Skip if vehicleType is being set from make selection to avoid interference
    if (isSettingVehicleTypeFromMakeRef.current) {
      return;
    }

    const currentKey = `${make || ""}-${makes.length}-${selectedMakeId || ""}`;

    // Skip if we've already processed this combination
    if (currentKey === makeLookupKeyRef.current) {
      return;
    }

    // Only process if we have makes data and a make name
    if (!make || makes.length === 0) {
      makeLookupKeyRef.current = currentKey;
      return;
    }

    makeLookupKeyRef.current = currentKey;

    if (!selectedMakeId) {
      // Try to find by name (case-insensitive)
      const foundMake = makes.find(
        (m) => m.name.toLowerCase() === make.toLowerCase(),
      );
      if (foundMake) {
        setSelectedMakeId(foundMake.id);
        // Set vehicleType if not set - for BOTH auto-loaded AND manual entry
        // This ensures Make section is visible when editing after manual entry
        if (!vehicleType) {
          const newVehicleType = foundMake.isMC ? "mc" : "car";
          isSettingVehicleTypeFromMakeRef.current = true;
          prevVehicleTypeRef.current = vehicleType || null;
          setVehicleType(newVehicleType);
          setTimeout(() => {
            prevVehicleTypeRef.current = newVehicleType;
            isSettingVehicleTypeFromMakeRef.current = false;
          }, 500);
        }
      } else {
        // If make not found but we have a make name (manual entry), default to car
        // This ensures Make section is visible when editing after manual entry
        if (make && !vehicleType) {
          const newVehicleType = "car";
          prevVehicleTypeRef.current = vehicleType || null;
          setVehicleType(newVehicleType);
          setTimeout(() => {
            prevVehicleTypeRef.current = newVehicleType;
            isSettingVehicleTypeFromMakeRef.current = false;
          }, 50);
        }
      }
    } else {
      // We have selectedMakeId, make sure the make exists in the loaded data
      const foundMake = makes.find((m) => m.id === selectedMakeId);
      // If not found, try to find by name as fallback
      if (!foundMake) {
        const foundByName = makes.find(
          (m) => m.name.toLowerCase() === make.toLowerCase(),
        );
        if (foundByName && foundByName.id !== selectedMakeId) {
          setSelectedMakeId(foundByName.id);
        }
      }
    }
  }, [make, selectedMakeId, makes, vehicleType, isAutoLoaded]);

  // Reset model when make changes (only if make actually changed from one value to another)
  const prevMakeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Skip if this is the first time make is being set (prevMakeRef is undefined)
    if (prevMakeRef.current === undefined) {
      prevMakeRef.current = make;
      return;
    }

    // Only reset model if make actually changed from one value to another (not empty to value)
    if (prevMakeRef.current && prevMakeRef.current !== make && make) {
      // Make changed from one value to another - reset model
      onModelSelect("");
      setShowModelOtherInput(false);
      setModelOtherText("");
    }

    // Update ref to current value
    prevMakeRef.current = make;
  }, [make, onModelSelect]);

  const handleVehicleTypeSelect = (type: "car" | "mc") => {
    // Mark that user manually changed vehicle type
    userChangedVehicleTypeRef.current = true;
    setVehicleType(type);
    // Notify parent about vehicleType change
    if (onVehicleTypeSelect) {
      onVehicleTypeSelect(type);
    }
    // Clear make and model - this will also clear Bildetaljer via handleSelectChange
    // We use a special empty string to signal that all Bildetaljer should be cleared
    onMakeSelect("");
    setSelectedMakeId(null);
    onModelSelect("");
    setShowMakeOtherInput(false);
    setMakeOtherText("");
    setShowModelOtherInput(false);
    setModelOtherText("");
  };

  const handleMakeSelect = (selectedMake: Make) => {
    // Block editing if auto-loaded
    if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
      onAutoLoadedFieldEditAttempt();
      return;
    }
    // CRITICAL: Set flag FIRST to prevent any useEffect from interfering
    // This must happen BEFORE any state changes
    const willSetVehicleType = !vehicleType;
    if (willSetVehicleType) {
      isSettingVehicleTypeFromMakeRef.current = true;
    }

    // Set make - this updates parent state immediately
    setSelectedMakeId(selectedMake.id);
    onMakeSelect(selectedMake.name);
    setShowMakeOtherInput(false);
    setMakeOtherText("");

    // Reset model immediately when make changes
    onModelSelect("");
    setShowModelOtherInput(false);
    setModelOtherText("");

    // Set vehicleType if not already set, based on make's isMC property
    // Do this AFTER setting make to ensure make is in state first
    if (willSetVehicleType) {
      const newVehicleType = selectedMake.isMC ? "mc" : "car";
      prevVehicleTypeRef.current = vehicleType || null;
      setVehicleType(newVehicleType);
      // Notify parent about vehicleType change
      if (onVehicleTypeSelect) {
        onVehicleTypeSelect(newVehicleType);
      }

      // Keep flag set for a LONG time to prevent vehicleType useEffect from interfering
      // This is critical - the flag must stay set until ALL useEffects have finished
      setTimeout(() => {
        prevVehicleTypeRef.current = newVehicleType;
        // Wait even longer before clearing flag to ensure vehicleType useEffect doesn't run
        setTimeout(() => {
          isSettingVehicleTypeFromMakeRef.current = false;
        }, 1000); // Long delay to ensure everything is settled
      }, 500);
    }
  };

  const handleMakeOtherClick = () => {
    setShowMakeOtherInput(true);
    setSelectedMakeId(null);
    onMakeSelect("");
    onModelSelect("");
    setShowModelOtherInput(false);
    setModelOtherText("");
  };

  const handleMakeOtherSubmit = () => {
    const submitted = normalizeOtherSubmitValue(makeOtherText);
    if (submitted) {
      onMakeSelect(submitted);
      setSelectedMakeId(null); // Clear selectedMakeId for "Övrigt"
      setShowMakeOtherInput(false);
      // Reset model when make changes via "Övrigt"
      onModelSelect("");
      setShowModelOtherInput(false);
      setModelOtherText("");
    }
  };

  const handleModelSelect = (modelName: string) => {
    // Block editing if auto-loaded
    if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
      onAutoLoadedFieldEditAttempt();
      return;
    }
    onModelSelect(modelName);
    setShowModelOtherInput(false);
    setModelOtherText("");
  };

  const handleModelOtherClick = () => {
    // Block editing if auto-loaded
    if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
      onAutoLoadedFieldEditAttempt();
      return;
    }
    setShowModelOtherInput(true);
    onModelSelect("");
  };

  const handleModelOtherSubmit = () => {
    const submitted = normalizeOtherSubmitValue(modelOtherText);
    if (submitted) {
      onModelSelect(submitted);
      setShowModelOtherInput(false);
    }
  };

  // Initialize vehicleType from make if auto-loaded and make exists
  useEffect(() => {
    if (isAutoLoaded && make && makes.length > 0 && !vehicleType) {
      isSettingVehicleTypeFromMakeRef.current = true;
      const foundMake = makes.find(
        (m) => m.name.toLowerCase() === make.toLowerCase(),
      );
      if (foundMake) {
        const newVehicleType = foundMake.isMC ? "mc" : "car";
        prevVehicleTypeRef.current = vehicleType || null;
        setVehicleType(newVehicleType);
        setTimeout(() => {
          prevVehicleTypeRef.current = newVehicleType;
          isSettingVehicleTypeFromMakeRef.current = false;
        }, 50);
        setSelectedMakeId(foundMake.id);
      } else {
        // If make not found, default to car
        const newVehicleType = "car";
        prevVehicleTypeRef.current = vehicleType || null;
        setVehicleType(newVehicleType);
        setTimeout(() => {
          prevVehicleTypeRef.current = newVehicleType;
          isSettingVehicleTypeFromMakeRef.current = false;
        }, 50);
      }
    }
  }, [isAutoLoaded, make, makes, vehicleType]);

  // Auto-select "Övrigt" if AutoUncle model doesn't exist in availableModels
  useEffect(() => {
    if (!model) return;
    if (
      shouldAutoExpandOtherForUnlistedModel({
        isAutoLoaded,
        model,
        hasCatalogMake: !!selectedMake,
        isLoadingMakes,
        availableModelNames: availableModels.map((m) => m.name),
        showOtherInput: showModelOtherInput,
      })
    ) {
      setShowModelOtherInput(true);
      setModelOtherText(model);
      // Note: We don't call onModelSelect here because model is already set from AutoUncle
      // The user can see that "Övrigt" is selected with the AutoUncle value pre-filled
    }
  }, [
    isAutoLoaded,
    model,
    selectedMake,
    isLoadingMakes,
    availableModels,
    showModelOtherInput,
  ]);

  // Auto-expand "Övrigt" when the looked-up MAKE matched no catalog entry
  // (e.g. LFO419 -> Ligier JS50). The effect above never runs then (no catalog
  // make, no models), so the fallback branch used to draw a highlighted Övrigt
  // chip with the input collapsed and empty even though the model was correctly
  // in form state. Mirror the unlisted-model behavior: expand pre-filled.
  useEffect(() => {
    if (!model) return;
    if (
      shouldAutoExpandOtherForUnknownMake({
        isAutoLoaded,
        value: model,
        hasCatalogMake: !!selectedMake,
        isLoadingMakes,
        makesLoaded: makes.length > 0,
        showOtherInput: showModelOtherInput,
      })
    ) {
      setShowModelOtherInput(true);
      setModelOtherText(model);
      // Note: We don't call onModelSelect here - the model is already in form state.
    }
  }, [
    isAutoLoaded,
    model,
    selectedMake,
    isLoadingMakes,
    makes,
    showModelOtherInput,
  ]);

  // Same treatment for the MAKE itself: an auto-loaded make that matched no
  // catalog entry (e.g. "Ligier") was only visible as a highlighted Övrigt chip
  // with a collapsed, empty input. Expand it pre-filled so the user can verify
  // the looked-up value.
  useEffect(() => {
    if (!make) return;
    if (
      shouldAutoExpandOtherForUnknownMake({
        isAutoLoaded,
        value: make,
        hasCatalogMake: !!selectedMake,
        isLoadingMakes,
        makesLoaded: makes.length > 0,
        showOtherInput: showMakeOtherInput,
      })
    ) {
      setShowMakeOtherInput(true);
      setMakeOtherText(make);
      // Note: We don't call onMakeSelect here - the make is already in form state.
    }
  }, [
    isAutoLoaded,
    make,
    selectedMake,
    isLoadingMakes,
    makes,
    showMakeOtherInput,
  ]);

  // Manual mode: show vehicle type selection and makes/models from API
  // Also show full form in auto-loaded mode for editing
  return (
    <div className="space-y-4">
      {/* Registration Number Input - Only shown in manual mode */}
      {isManualMode && (
        <div className="space-y-2">
          <h3 className="font-medium text-sm">Registreringsnummer</h3>
          <Input
            value={regNumber}
            onChange={(e) => {
              let value = e.target.value.toUpperCase();
              // Remove spaces and dashes
              value = value.replace(/\s/g, "").replace(/-/g, "");
              onRegNumberChange?.(value);
            }}
            placeholder={valuationLabels(language).registration.placeholderWithExample}
            className={`w-full ${
              regNumber &&
              !validateRegistrationFormatAny(regNumber) &&
              "border-red-500 focus-visible:ring-red-500"
            }`}
            maxLength={6}
          />
          {regNumber && !validateRegistrationFormatAny(regNumber) && (
            <p className="text-xs text-red-600">
              Ogiltigt format. Använd t.ex. ABC123, ABC12D, XXX12X eller AB12345
            </p>
          )}
        </div>
      )}

      {/* Vehicle Type Selection - Only show if regNumber is valid in manual mode, or always in auto-loaded mode */}
      {((isManualMode &&
        regNumber &&
        validateRegistrationFormatAny(regNumber)) ||
        !isManualMode ||
        isAutoLoaded) && (
        <div className="space-y-2">
          <h3 className="font-medium text-sm">{vLabels.vehicleType}</h3>
          <div className="grid grid-cols-2 gap-1.5">
            <Button
              variant="formOption"
              onClick={() => handleVehicleTypeSelect("car")}
              className={`${
                vehicleType === "car"
                  ? "border-accent border-2 bg-accent/20"
                  : "border-gray-400"
              }`}
            >
              {vLabels.car}
            </Button>
            <Button
              variant="formOption"
              onClick={() => handleVehicleTypeSelect("mc")}
              className={`${
                vehicleType === "mc"
                  ? "border-accent border-2 bg-accent/20"
                  : "border-gray-400"
              }`}
            >
              {vLabels.motorcycle}
            </Button>
          </div>
        </div>
      )}

      {/* Make Selection - show even if auto-loaded (for editing) */}
      {/* In manual mode, only show if regNumber is valid */}
      {((isManualMode &&
        regNumber &&
        validateRegistrationFormatAny(regNumber)) ||
        !isManualMode ||
        isAutoLoaded) &&
        (vehicleType || isAutoLoaded) && (
          <div className="space-y-2">
            <h3 className="font-medium text-sm">{vLabels.make}</h3>
            {isLoadingMakes ? (
              <div className="text-sm text-muted-foreground">
                {vLabels.loadingMakes}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {displayedMakes.map((makeItem) => (
                    <Button
                      key={makeItem.id}
                      variant="formOption"
                      onClick={() => handleMakeSelect(makeItem)}
                      className={`${
                        selectedMakeId === makeItem.id || make === makeItem.name
                          ? "border-accent border-2 bg-accent/20"
                          : "border-gray-400"
                      } whitespace-normal break-words min-h-[2.5rem] py-2 text-center leading-tight`}
                      title={makeItem.name}
                    >
                      {makeItem.name}
                    </Button>
                  ))}
                  {/* Övrigt option */}
                  {!showMakeOtherInput && (
                    <Button
                      variant="formOption"
                      onClick={handleMakeOtherClick}
                      className={
                        make &&
                        !selectedMakeId &&
                        !makes.find((m) => m.name === make)
                          ? "border-accent border-2 bg-accent/20"
                          : "border-gray-400"
                      }
                    >
                      {vLabels.other}
                    </Button>
                  )}
                </div>

                {showMakeOtherInput && (
                  <div className="flex gap-2">
                    <Input
                      value={makeOtherText}
                      onChange={(e) => setMakeOtherText(e.target.value)}
                      placeholder={vLabels.enterMake}
                      maxLength={30}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleMakeOtherSubmit();
                        }
                      }}
                    />
                    <Button
                      onClick={handleMakeOtherSubmit}
                      disabled={!makeOtherText.trim()}
                    >
                      {vLabels.save}
                    </Button>
                  </div>
                )}

                {/* Only show "Mer" button for cars, not for motorcycles */}
                {vehicleType !== "mc" && otherMakes.length > 0 && (
                  <Button
                    variant="ghost"
                    className="w-full flex justify-center gap-2 pt-2"
                    onClick={() => setShowAllMakes(!showAllMakes)}
                  >
                    {showAllMakes ? vLabels.showLess : vLabels.more}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        showAllMakes ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                )}
              </>
            )}
          </div>
        )}

      {/* Model Selection - ALWAYS show when make is selected */}
      {/* In manual mode, only show if regNumber is filled */}
      {((isManualMode && regNumber) || !isManualMode || isAutoLoaded) &&
        make && (
          <div className="space-y-2">
            <h3 className="font-medium text-sm">{vLabels.model}</h3>

            {/* Show warning if AutoUncle didn't provide model */}
            {isAutoLoaded && !model && (
              <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  {vLabels.autoLoadModelError}
                </AlertDescription>
              </Alert>
            )}

            {/* Show loading state */}
            {isLoadingMakes && (
              <div className="text-sm text-muted-foreground">
                {selectedMakeId
                  ? vLabels.loadingModelsFor.replace("{make}", make)
                  : vLabels.loadingModels}
              </div>
            )}

            {/* Show models if selectedMake is found and has models */}
            {!isLoadingMakes && selectedMake && availableModels.length > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                  {displayedModels.map((modelItem) => (
                    <Button
                      variant="formOption"
                      key={modelItem.id}
                      onClick={() => handleModelSelect(modelItem.name)}
                      className={`${
                        model === modelItem.name
                          ? "border-accent border-2 bg-accent/20"
                          : "border-gray-400"
                      } whitespace-normal break-words min-h-[2.5rem] py-2 text-center leading-tight`}
                      title={modelItem.name}
                    >
                      {modelItem.name}
                    </Button>
                  ))}
                  {/* Övrigt option */}
                  {!showModelOtherInput && (
                    <Button
                      variant="formOption"
                      onClick={handleModelOtherClick}
                      className={
                        model &&
                        availableModels.length > 0 &&
                        !availableModels.some((m) => m.name === model)
                          ? "border-accent border-2 bg-accent/20"
                          : "border-gray-400"
                      }
                    >
                      {vLabels.other}
                    </Button>
                  )}
                </div>
                {showModelOtherInput && (
                  <div className="flex gap-2">
                    <Input
                      value={modelOtherText}
                      onChange={(e) => setModelOtherText(e.target.value)}
                      placeholder={vLabels.enterModel}
                      maxLength={30}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleModelOtherSubmit();
                        }
                      }}
                    />
                    <Button
                      onClick={handleModelOtherSubmit}
                      disabled={!modelOtherText.trim()}
                    >
                      {vLabels.save}
                    </Button>
                  </div>
                )}
                {availableModels.length > 4 && (
                  <Button
                    variant="ghost"
                    className="w-full mt-2 text-sm flex items-center justify-center gap-2 pt-2"
                    onClick={() => setShowAllModels(!showAllModels)}
                  >
                    {showAllModels ? vLabels.showLess : vLabels.more}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        showAllModels ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                )}
              </>
            )}

            {/* Show Övrigt if selectedMake not found OR no models available */}
            {!isLoadingMakes &&
              (!selectedMake ||
                (selectedMake && availableModels.length === 0)) && (
                <>
                  {!showModelOtherInput ? (
                    <Button
                      variant="formOption"
                      onClick={handleModelOtherClick}
                      className={
                        isFallbackOtherChipSelected(model)
                          ? "bg-blue-200 dark:bg-blue-900/30 border-blue-400"
                          : "border-gray-400"
                      }
                    >
                      {vLabels.other}
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={modelOtherText}
                        onChange={(e) => setModelOtherText(e.target.value)}
                        placeholder={vLabels.enterModel}
                        maxLength={30}
                        className="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleModelOtherSubmit();
                          }
                        }}
                      />
                      <Button
                        onClick={handleModelOtherSubmit}
                        disabled={!modelOtherText.trim()}
                      >
                        {vLabels.save}
                      </Button>
                    </div>
                  )}
                </>
              )}
          </div>
        )}

      {/* Year Selection */}
      {/* In manual mode, only show if regNumber is filled */}
      {((isManualMode && regNumber) || !isManualMode || isAutoLoaded) &&
        make &&
        model && (
          <div className="space-y-2">
            <h3 className="font-medium text-sm">{vLabels.modelYear}</h3>
            <div className="grid grid-cols-4 md:grid-cols-5 gap-1.5">
              {(showAllYears ? YEARS : YEARS.slice(0, 10)).map((yearValue) => (
                <Button
                  key={yearValue}
                  variant="formOption"
                  onClick={() => {
                    if (isAutoLoaded && onAutoLoadedFieldEditAttempt) {
                      onAutoLoadedFieldEditAttempt();
                      return;
                    }
                    onYearSelect(parseInt(yearValue, 10));
                  }}
                  className={`${
                    year?.toString() === yearValue ||
                    parseInt(yearValue, 10) === year
                      ? "border-accent border-2 bg-accent/20"
                      : "border-gray-400"
                  } truncate`}
                  title={yearValue}
                >
                  {yearValue}
                </Button>
              ))}
            </div>
            {YEARS.length > 10 && (
              <Button
                variant="ghost"
                className="w-full flex justify-center gap-2 pt-2"
                onClick={() => setShowAllYears(!showAllYears)}
              >
                {showAllYears ? vLabels.showLess : vLabels.more}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    showAllYears ? "rotate-180" : ""
                  }`}
                />
              </Button>
            )}
          </div>
        )}
    </div>
  );
};
